import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, BookUser, X } from 'lucide-react';
import toast from 'react-hot-toast';
import '../styles/enterprise.css';
import '../styles/responsive/pages/AddressBook.css';
import { useTheme } from '../context/ThemeContext';
import PageHeaderPro from '../components/common/PageHeaderPro';
import StatusBadge from '../components/common/StatusBadge';
import { TableSkeleton } from '../components/common/Skeleton';
import {
  useGetAddressesQuery,
  useAddAddressMutation,
  useDeleteAddressMutation,
} from '../store/api/adminOpsApi';
import { truncateHash } from '../utils/formatAmount';

const fmtDateTime = (v) => {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// A cooldown is active when cooldown_until is in the future.
const inCooldown = (until) => until && new Date(until).getTime() > Date.now();

const AddressBook = () => {
  const { mode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const merchantId = searchParams.get('merchantId') || '';

  const [merchantInput, setMerchantInput] = useState(merchantId);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ address: '', networkId: '', label: '' });

  const { data: addresses = [], isLoading, isFetching } = useGetAddressesQuery(merchantId, {
    skip: !merchantId,
    refetchOnMountOrArgChange: true,
  });
  const [addAddress, { isLoading: adding }] = useAddAddressMutation();
  const [deleteAddress] = useDeleteAddressMutation();

  const applyMerchant = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (merchantInput) next.set('merchantId', merchantInput);
    else next.delete('merchantId');
    setSearchParams(next, { replace: true });
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!form.address.trim()) {
      toast.error('Address is required');
      return;
    }
    try {
      await addAddress({
        id: merchantId,
        address: form.address.trim(),
        networkId: form.networkId || undefined,
        label: form.label || undefined,
      }).unwrap();
      toast.success('Address whitelisted');
      setForm({ address: '', networkId: '', label: '' });
      setShowAdd(false);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to add address');
    }
  };

  const handleDelete = async (addressId, address) => {
    if (!window.confirm(`Remove ${truncateHash(address, 8, 6)} from the whitelist?`)) return;
    try {
      await deleteAddress({ id: merchantId, addressId }).unwrap();
      toast.success('Address removed');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to remove address');
    }
  };

  return (
    <div className="ent ent-page address-book-page">
      <PageHeaderPro
        title="Address Book"
        mode={mode === 'test' ? 'test' : 'live'}
        updatedText={isFetching ? 'Updating…' : merchantId ? `Merchant ${merchantId}` : 'Pick a merchant'}
        actions={
          merchantId ? (
            <button className="ent-btn ent-btn-primary" onClick={() => setShowAdd(true)}>
              <Plus size={16} /> Add address
            </button>
          ) : null
        }
      />

      <form className="ab-merchant-bar" onSubmit={applyMerchant}>
        <label>
          <span>Merchant ID</span>
          <input
            type="text"
            value={merchantInput}
            onChange={(e) => setMerchantInput(e.target.value)}
            placeholder="Enter merchant id"
          />
        </label>
        <button type="submit" className="ent-btn ent-btn-secondary">
          Load
        </button>
      </form>

      <div className="pro-table-wrap">
        {!merchantId ? (
          <div className="empty-state">
            <BookUser size={48} />
            <p>No merchant selected</p>
            <span>Enter a merchant id to view their whitelisted payout addresses</span>
          </div>
        ) : isLoading ? (
          <TableSkeleton rows={5} cols={5} />
        ) : addresses.length === 0 ? (
          <div className="empty-state">
            <BookUser size={48} />
            <p>No whitelisted addresses</p>
            <span>Add a destination address to whitelist it for payouts</span>
          </div>
        ) : (
          <table className="pro-table">
            <thead>
              <tr>
                <th>ADDRESS</th>
                <th>LABEL</th>
                <th>NETWORK</th>
                <th>COOLDOWN</th>
                <th className="col-actions">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {addresses.map((a) => {
                const cooling = inCooldown(a.cooldown_until || a.cooldownUntil);
                const until = a.cooldown_until || a.cooldownUntil;
                return (
                  <tr key={a.id}>
                    <td data-label="ADDRESS">
                      <span className="mono ab-address">{a.address}</span>
                    </td>
                    <td data-label="LABEL">
                      <span className="cell-2__main">{a.label || '—'}</span>
                    </td>
                    <td data-label="NETWORK">
                      <span className="cell-2__sub">{a.networkName || a.network_id || a.networkId || '—'}</span>
                    </td>
                    <td data-label="COOLDOWN">
                      {cooling ? (
                        <StatusBadge status="pending" label={`Until ${fmtDateTime(until)}`} dot />
                      ) : (
                        <StatusBadge status="active" label="Cleared" dot />
                      )}
                    </td>
                    <td className="col-actions" data-label="ACTIONS">
                      <button
                        className="ent-btn ent-btn-secondary ent-btn--xs ab-del"
                        onClick={() => handleDelete(a.id, a.address)}
                        aria-label="Remove address"
                      >
                        <Trash2 size={14} /> Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <>
          <div className="ab-modal-overlay" onClick={() => setShowAdd(false)} />
          <div className="ab-modal" role="dialog" aria-modal="true" aria-label="Add address">
            <div className="ab-modal-header">
              <h3>Whitelist address</h3>
              <button className="ab-close" onClick={() => setShowAdd(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <form className="ab-modal-body" onSubmit={submitAdd}>
              <div className="rd-field">
                <label className="rd-label">Address</label>
                <input
                  className="rd-input"
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="0x… / destination address"
                />
              </div>
              <div className="rd-field">
                <label className="rd-label">
                  Network ID <span className="rd-optional">(optional)</span>
                </label>
                <input
                  className="rd-input"
                  type="text"
                  value={form.networkId}
                  onChange={(e) => setForm((f) => ({ ...f, networkId: e.target.value }))}
                  placeholder="e.g. 1"
                />
              </div>
              <div className="rd-field">
                <label className="rd-label">
                  Label <span className="rd-optional">(optional)</span>
                </label>
                <input
                  className="rd-input"
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Cold storage"
                />
              </div>
              <div className="ab-modal-footer">
                <button type="button" className="ent-btn ent-btn-secondary" onClick={() => setShowAdd(false)}>
                  Cancel
                </button>
                <button type="submit" className="ent-btn ent-btn-primary" disabled={adding}>
                  {adding ? 'Adding…' : 'Add address'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default AddressBook;
